import Error from '../shared/error';
import { APIErrorType } from '../../api';
import Loader from '../shared/loader';
import { useNavigate, useParams } from 'react-router-dom';
import { useExamStudentQuestionsInfiniteQuery } from '../../hooks/react-query/use-student-question';
import {
  useFinishStudentExamMutation,
  useStudentExam
} from '../../hooks/react-query/use-student-exam';
import ExamHeader from './exam-header';
import ExamTimer from './exam-timer';
import Segment from '../shared/segment';
import Question from './question';
import Actions from './actions';
import LessonTitle from './lesson-title';
import { StudentQuestionOverviewType } from '../../api/student-question';
import useBeforeUnload from '../../hooks/index/use-before-unload';
import BehaviorStateModal from './question/behavior-state-modal';
import { useBooleanState } from '../../hooks/index/use-boolean-state';

function ExamContainer() {
  const navigate = useNavigate();
  const { examId } = useParams();
  const {
    isError: studentExamIsError,
    error: studentExamError,
    isLoading: studentExamIsLoading,
    data: studentExam
  } = useStudentExam(examId ? parseInt(examId) : undefined);
  const {
    isError: studentQuestionsIsError,
    error: studentQuestionsError,
    isLoading: studentQuestionsIsLoading,
    data: studentQuestionsData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useExamStudentQuestionsInfiniteQuery<StudentQuestionOverviewType>(
    examId ? parseInt(examId) : undefined
  );
  const { mutateAsync: finishStudentExam } = useFinishStudentExamMutation();
  const { value: showBehaviorStateModal, toggle: toggleShowBehaviorStateModal } =
    useBooleanState(false);

  useBeforeUnload(event => {
    event.preventDefault();
    const confirmationMessage = 'confirm exit is being called';
    event.returnValue = confirmationMessage;
    return confirmationMessage;
  });

  if (!examId) return <Error />;
  if (studentExamIsError) {
    const error = studentExamError as APIErrorType;
    return error.status === 403 ? (
      <Error errorMessage='شما دسترسی به این آزمون ندارید.' />
    ) : (
      <Error error={error} />
    );
  }
  if (studentQuestionsIsError) return <Error error={studentQuestionsError as APIErrorType} />;
  if (studentExamIsLoading || studentQuestionsIsLoading || !studentExam || !studentQuestionsData)
    return <Loader />;

  if (studentExam.status !== 1)
    return <Error errorMessage='شما دسترسی به سوالات این آزمون ندارید.' />;

  const handleFinish = async () => {
    await finishStudentExam(studentExam.id);
    navigate('/results');
  };

  let lastLessonId: number | null = null;

  return (
    <div className='mx-2 flex flex-col gap-1'>
      <ExamHeader
        exam={studentExam.exam}
        timer={<ExamTimer remainSeconds={studentExam.remainSeconds} onFinish={handleFinish} />}
      />
      <Segment bg noMargin>
        {studentQuestionsData.pages.map((page, pageIndex) => (
          <div key={pageIndex}>
            {page.data.map((studentQuestion, studentQuestionIndex) => {
              const hasNewLesson = studentQuestion.question.lesson.id !== lastLessonId;
              lastLessonId = studentQuestion.question.lesson.id;

              return (
                <div key={studentQuestion.id}>
                  {studentExam.exam.lessonCount > 1 && hasNewLesson && (
                    <LessonTitle lesson={studentQuestion.question.lesson} />
                  )}
                  <Question
                    question={studentQuestion.question}
                    questionNumber={10 * pageIndex + studentQuestionIndex + 1}
                    questionCount={studentExam.exam.questionCount}
                    studentQuestionId={studentQuestion.id}
                    selectedOptionId={studentQuestion.option}
                    behaviorState={studentQuestion.behaviorState}
                    toggleShowBehaviorStateModal={toggleShowBehaviorStateModal}
                  />
                </div>
              );
            })}
          </div>
        ))}
        {isFetchingNextPage && <Loader />}
      </Segment>
      <Segment bg noMargin>
        <Actions
          studentExamId={studentExam.id}
          hasNextPage={hasNextPage}
          onFetchNext={fetchNextPage}
        />
      </Segment>
      <BehaviorStateModal
        showModal={showBehaviorStateModal}
        onClose={toggleShowBehaviorStateModal}
      />
    </div>
  );
}

export default ExamContainer;
